import api from './api';

/**
 * 定时SOP服务
 * 提供与后端定时SOP API的交互
 */
const scheduledSopService = {
  /**
   * 获取所有定时SOP模板
   * @param {number} tenantId 租户ID
   * @param {string} sortBy 排序字段，默认为createdAt
   * @param {string} sortOrder 排序方向，默认为desc
   * @returns {Promise<Array>} 定时SOP模板列表
   */
  getAllScheduledSopTemplates: async (tenantId, sortBy = 'createdAt', sortOrder = 'desc') => {
    console.log('获取定时SOP模板列表，排序参数:', { sortBy, sortOrder });
    
    try {
      const response = await api.get('/v1/sop/scheduled/templates', {
        headers: {
          'X-Tenant-ID': tenantId
        },
        params: {
          sortBy,
          sortOrder
        }
      });
      console.log('定时SOP模板列表响应:', response.data);
      return response.data;
    } catch (error) {
      console.error('获取定时SOP模板列表失败:', error);
      throw error;
    }
  },

  /**
   * 获取定时SOP模板详情
   * @param {number} templateId 模板ID
   * @param {number} tenantId 租户ID
   * @returns {Promise<Object>} 定时SOP模板详情
   */
  getScheduledSopDetail: async (templateId, tenantId) => {
    console.log(`获取定时SOP模板详情，ID: ${templateId}`);
    
    try {
      const response = await api.get(`/v1/sop/scheduled/management/${templateId}`, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('定时SOP模板详情响应:', response.data);
      return response.data;
    } catch (error) {
      console.error(`获取定时SOP模板(ID=${templateId})详情失败:`, error);
      throw error;
    }
  },

  /**
   * 创建定时SOP模板和步骤
   * @param {Object} data 包含模板、步骤和目标规则的数据
   * @param {number} tenantId 租户ID
   * @returns {Promise<Object>} 创建结果
   */
  createScheduledSopWithSteps: async (data, tenantId) => {
    console.log('创建定时SOP:', data);
    
    if (data.steps && data.steps.length > 0) {
      console.log('定时SOP步骤数量:', data.steps.length);
      console.log('步骤详情示例:', data.steps[0]);
      
      const stepsWithTargetRules = data.steps.filter(step => step.targetRules && step.targetRules.length > 0);
      console.log('包含步骤级别目标规则的步骤数:', stepsWithTargetRules.length);
      
      const stepsWithEmployees = data.steps.filter(step => step.employeeIds && step.employeeIds.length > 0);
      console.log('包含步骤级别执行人的步骤数:', stepsWithEmployees.length);
    }
    
    try {
      const response = await api.post('/v1/sop/scheduled/management', data, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('创建定时SOP响应:', response.data);
      return response.data;
    } catch (error) {
      console.error('创建定时SOP失败:', error);
      throw error;
    }
  },

  /**
   * 更新定时SOP模板和步骤
   * @param {number} templateId 模板ID
   * @param {Object} data 包含模板、步骤和目标规则的数据
   * @param {number} tenantId 租户ID
   * @returns {Promise<Object>} 更新结果
   */
  updateScheduledSopWithSteps: async (templateId, data, tenantId) => {
    console.log(`更新定时SOP ID: ${templateId}`, data);
    
    if (data.steps && data.steps.length > 0) {
      console.log('定时SOP步骤数量:', data.steps.length);
      console.log('步骤详情示例:', data.steps[0]);
      
      const stepsWithTargetRules = data.steps.filter(step => step.targetRules && step.targetRules.length > 0);
      console.log('包含步骤级别目标规则的步骤数:', stepsWithTargetRules.length);
      
      const stepsWithEmployees = data.steps.filter(step => step.employeeIds && step.employeeIds.length > 0);
      console.log('包含步骤级别执行人的步骤数:', stepsWithEmployees.length);
    }
    
    try {
      const response = await api.put(`/v1/sop/scheduled/management/${templateId}`, data, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('更新定时SOP响应:', response.data);
      return response.data;
    } catch (error) {
      console.error(`更新定时SOP模板(ID=${templateId})失败:`, error);
      throw error;
    }
  },

  /**
   * 删除定时SOP
   */
  deleteScheduledSop: async (id, tenantId) => {
    console.log(`删除定时SOP ID: ${id}`);
    
    try {
      const response = await api.delete(`/v1/sop/scheduled/management/${id}`, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('删除定时SOP响应:', response.status);
      return response.status === 200 || response.status === 204;
    } catch (error) {
      console.error('删除定时SOP错误:', error);
      throw error;
    }
  },

  /**
   * 激活定时SOP模板
   * @param {number} templateId 模板ID
   * @param {number} tenantId 租户ID
   * @returns {Promise<Object>} 激活结果
   */
  activateScheduledSop: async (templateId, tenantId) => {
    console.log(`激活定时SOP ID: ${templateId}`);
    
    try {
      const response = await api.put(`/v1/sop/scheduled/management/${templateId}/activate`, {}, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('激活定时SOP响应:', response.data);
      return response.data;
    } catch (error) {
      console.error(`激活定时SOP模板(ID=${templateId})失败:`, error);
      throw error;
    }
  },

  /**
   * 禁用定时SOP模板
   * @param {number} templateId 模板ID
   * @param {number} tenantId 租户ID
   * @returns {Promise<Object>} 禁用结果
   */
  deactivateScheduledSop: async (templateId, tenantId) => {
    console.log(`禁用定时SOP ID: ${templateId}`);
    
    try {
      const response = await api.put(`/v1/sop/scheduled/management/${templateId}/deactivate`, {}, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('禁用定时SOP响应:', response.data);
      return response.data;
    } catch (error) {
      console.error(`禁用定时SOP模板(ID=${templateId})失败:`, error);
      throw error;
    }
  },

  /**
   * 复制定时SOP
   */
  copyScheduledSop: async (id, data, tenantId) => {
    console.log(`复制定时SOP ID: ${id}`, data);
    
    try {
      const response = await api.post(`/v1/sop/scheduled/management/${id}/copy`, data, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('复制定时SOP响应:', response.data);
      return response.data;
    } catch (error) {
      console.error('复制定时SOP错误:', error);
      throw error;
    }
  },

  /**
   * 获取定时SOP执行情况
   * @param {number} templateId 模板ID
   * @param {number} tenantId 租户ID
   * @returns {Promise<Object>} 执行情况结果
   */
  getScheduledSopExecutionStatus: async (templateId, tenantId) => {
    console.log(`获取定时SOP执行状态 ID: ${templateId}`);
    
    try {
      const response = await api.get(`/v1/sop/scheduled/management/${templateId}/execution-status`, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('获取定时SOP执行状态响应:', response.data);
      return response.data;
    } catch (error) {
      console.error('获取定时SOP执行状态错误:', error);
      throw error;
    }
  },

  /**
   * 为定时SOP分配企业微信成员
   */
  assignEmployees: async (templateId, employeeIds, tenantId) => {
    console.log(`为定时SOP分配企业微信成员 ID: ${templateId}`, employeeIds);
    
    try {
      const response = await api.post(`/v1/sop/scheduled/management/${templateId}/assign-employees`, 
        { employeeIds }, 
        {
          headers: {
            'X-Tenant-ID': tenantId
          }
        }
      );
      console.log('定时SOP分配企业微信成员响应:', response.data);
      return response.data;
    } catch (error) {
      console.error('定时SOP分配企业微信成员错误:', error);
      throw error;
    }
  },

  /**
   * 获取定时SOP已分配的企业微信成员
   */
  getAssignedEmployees: async (templateId, tenantId) => {
    console.log(`获取定时SOP已分配的企业微信成员 ID: ${templateId}`);
    
    try {
      const response = await api.get(`/v1/sop/scheduled/management/${templateId}/assigned-employees`, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('获取定时SOP已分配的企业微信成员响应:', response.data);
      return response.data;
    } catch (error) {
      console.error('获取定时SOP已分配的企业微信成员错误:', error);
      throw error;
    }
  },

  /**
   * 获取定时SOP执行记录
   * @param {number} templateId 模板ID
   * @param {number} tenantId 租户ID
   * @param {Object} params 查询参数
   * @returns {Promise<Object>} 执行记录
   */
  getScheduledSopExecutionRecords: async (templateId, tenantId, params = {}) => {
    console.log(`获取定时SOP执行记录 ID: ${templateId}`, params);
    
    try {
      const response = await api.get(`/v1/sop/scheduled/management/${templateId}/executions`, {
        headers: {
          'X-Tenant-ID': tenantId
        },
        params
      });
      console.log('获取定时SOP执行记录响应:', response.data);
      return response.data;
    } catch (error) {
      console.error(`获取定时SOP模板(ID=${templateId})执行记录失败:`, error);
      throw error;
    }
  },
  
  /**
   * 检查定时SOP是否有执行实例
   */
  checkHasExecutions: async (id, tenantId) => {
    console.log(`检查定时SOP是否有执行实例 ID: ${id}`);
    
    try {
      const response = await api.get(`/v1/sop/scheduled/management/${id}/has-executions`, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('检查定时SOP执行实例响应:', response.data);
      return response.data;
    } catch (error) {
      console.error('检查定时SOP执行实例错误:', error);
      throw error;
    }
  },

  /**
   * 手动生成定时SOP步骤
   * @param {number} templateId 模板ID
   * @param {number} tenantId 租户ID
   * @returns {Promise<Object>} 生成结果
   */
  generateScheduledSopSteps: async (templateId, tenantId) => {
    try {
      const response = await api.post(`/v1/sop/scheduled/management/${templateId}/generate-steps`, {}, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      return response.data;
    } catch (error) {
      console.error(`手动生成定时SOP模板(ID=${templateId})步骤失败:`, error);
      throw error;
    }
  }
};

export default scheduledSopService; 